import { useState } from 'react';
import type { LigneVivier, Vivier } from '../partage/api/types';
import { teinteDuProjet } from '../apparence';
import { Etoiles } from './Etoiles';
import {
  C_EST_FAIT,
  caLibere,
  DANS_MA_JOURNEE,
  DEBLOQUANT,
  GARDER_POUR_AUJOURDHUI,
  HEURES_D_OUVERTURE,
  proprietesEnMots,
  SI_TU_N_EN_FAIS_QU_UNE,
  UNE_AUTRE,
} from '../partage/vocabulaire';
import './Debloquant.css';

type UnDebloquant = Vivier['debloquants'][number];
type Libere = UnDebloquant['libere'][number];

/** Combien de choses libérées la fiche montre en pastilles, avant « Et N autres ». */
const MONTREES = 3;

/**
 * Ce que « Garder pour aujourd'hui » montre : le bouton, « Dans ma journée » une fois gardée, ou
 * rien quand le serveur la refuse (un rendez-vous a déjà son heure ; une étape dont tout
 * l'intérieur est fait), décisions d'Igor du 2026-10-03.
 */
export type Garder = 'bouton' | 'dans-la-journee' | 'cache';

/**
 * Le débloquant de « Maintenant », en grande fiche scotchée en haut à gauche (chantier
 * maintenant-en-etages, modification 01 ; maquette du carnet `maintenant.html`, `.debloquant`),
 * bordée à gauche de la couleur de son projet.
 *
 * Le titre écrit à la main, ses réglages sur une ligne (projet, durée, lieu, heures d'ouverture,
 * étoiles), la ligne qui dit en quoi il ne colle pas à « J'ai », « Je suis » ou « Courage » (il
 * reste quels que soient les réglages, ticket 10 de flottaison-suite), sa marge ; dessous, ce
 * qu'elle libère, en pastilles ; puis trois gestes : « C'est fait »,
 * « Une autre » (le débloquant suivant, en boucle ; absent quand il n'y en a qu'un) et « Garder
 * pour aujourd'hui » (elle entre dans « Ma journée »).
 *
 * L'ordre est la thèse : on lit de l'effort vers ce qu'il libère, jamais l'inverse. Le
 * débloquant signale, il ne reclasse rien : la bulle reste aussi dans « Pendant ce temps »
 * quand elle passe les réglages.
 */
export function Debloquant({
  debloquant,
  ecart,
  marge,
  autres,
  garder,
  occupe,
  onOuvrir,
  onFait,
  onUneAutre,
  onGarder,
}: {
  debloquant: UnDebloquant | null;
  /**
   * « Plus long que tes 30 min » : ce qui ne colle pas aux réglages, en une ligne
   * (`ligneDesEcarts`, ticket 10 de flottaison-suite) ; `null` quand il y colle.
   */
  ecart: string | null;
  /** « Marge de 5 jours » : le pourquoi de sa ligne, choisi par `margeDuDebloquant`. */
  marge: string | null;
  /** Il y en a d'autres : « Une autre » se montre. */
  autres: boolean;
  garder: Garder;
  /** Un geste est parti : les boutons attendent la réponse (deux appuis rapprochés se marchaient dessus). */
  occupe: boolean;
  onOuvrir: (id: string) => void;
  onFait: (bulle: LigneVivier) => void;
  onUneAutre: () => void;
  onGarder: (bulle: LigneVivier) => void;
}) {
  const [deroule, setDeroule] = useState(false);
  if (!debloquant) return null;

  const { bulle, libere } = debloquant;
  const montres = libere.slice(0, MONTREES);
  const reste = libere.slice(MONTREES);

  return (
    <section
      className="debloquant"
      aria-label={DEBLOQUANT}
      data-tuto="debloquant-premier"
      style={{ ['--teinte' as string]: teinteDuProjet(bulle.dossier?.couleur) }}
    >
      <span className="debloquant-scotch" aria-hidden="true" />
      <p className="debloquant-etiquette">
        {DEBLOQUANT} · {SI_TU_N_EN_FAIS_QU_UNE}
      </p>

      <div className="debloquant-haut">
        <span className="debloquant-galet" aria-hidden="true" />
        <div className="debloquant-quoi">
          <button type="button" className="debloquant-titre" title={bulle.titre} onClick={() => onOuvrir(bulle.id)}>
            {bulle.titre}
          </button>
          <ReglagesEnLigne bulle={bulle} />
          {ecart && <p className="debloquant-ecart">{ecart}</p>}
          {marge && <p className="debloquant-marge">{marge}</p>}
        </div>
      </div>

      <div className="debloquant-libere">
        <span>{caLibere(libere.length)}</span>
        {montres.map((l) => (
          <Pastille key={l.id} libere={l} />
        ))}
        {reste.length > 0 && (
          <EtNAutres combien={reste.length} deroule={deroule} onBasculer={() => setDeroule(!deroule)} />
        )}
      </div>

      {deroule && <Reste libere={reste} />}

      <div className="debloquant-gestes">
        <button type="button" className="debloquant-fait" disabled={occupe} onClick={() => onFait(bulle)}>
          {C_EST_FAIT}
        </button>
        {autres && (
          <button type="button" className="debloquant-geste" onClick={onUneAutre}>
            {UNE_AUTRE}
          </button>
        )}
        {garder === 'bouton' && (
          <button type="button" className="debloquant-geste" disabled={occupe} onClick={() => onGarder(bulle)}>
            {GARDER_POUR_AUJOURDHUI}
          </button>
        )}
        {garder === 'dans-la-journee' && (
          <span className="debloquant-garde" role="status">
            ✓ {DANS_MA_JOURNEE}
          </span>
        )}
      </div>
    </section>
  );
}

/**
 * Ses réglages sur une ligne, comme la maquette (`.d-info`) : le point de son projet, puis le
 * projet, la durée, le lieu, les heures d'ouverture, les étoiles. Les heures d'ouverture y gardent
 * leur mot de pastille, « Heures d'ouverture ».
 */
function ReglagesEnLigne({ bulle }: { bulle: LigneVivier }) {
  const mots = [
    bulle.dossier?.titre,
    ...proprietesEnMots(bulle, ['duree', 'lieu']),
    bulle.heuresOuvrables ? HEURES_D_OUVERTURE : null,
  ].filter((m): m is string => Boolean(m));
  if (mots.length === 0 && !bulle.charge) return null;
  return (
    <p className="debloquant-info">
      <i className="debloquant-point" aria-hidden="true" />
      {mots.join(' · ')}
      {bulle.charge ? (
        <>
          {mots.length > 0 ? ' · ' : null}
          <Etoiles charge={bulle.charge} />
        </>
      ) : null}
    </p>
  );
}

/** Une chose libérée. Un titre long se coupe par « … » ; il se lit entier au survol. */
function Pastille({ libere }: { libere: Libere }) {
  return (
    <span className="debloquant-chose" title={libere.titre}>
      {libere.titre}
    </span>
  );
}

function EtNAutres({
  combien,
  deroule,
  onBasculer,
}: {
  combien: number;
  deroule: boolean;
  onBasculer: () => void;
}) {
  return (
    <button type="button" className="debloquant-autres" aria-expanded={deroule} onClick={onBasculer}>
      Et {combien} {combien > 1 ? 'autres' : 'autre'} {deroule ? '▴' : '▾'}
    </button>
  );
}

/** Le reste de ce qui se libère, déroulé dans la carte, sous la ligne qui l'annonce. */
function Reste({ libere }: { libere: Libere[] }) {
  return (
    <ul className="debloquant-reste">
      {libere.map((l) => (
        <li key={l.id}>{l.titre}</li>
      ))}
    </ul>
  );
}
